'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Search, LayoutDashboard, Users, Phone, Calendar, BarChart2, Settings, History, Bell, MessageSquare, FileText, FlaskConical, Zap, Shield, Building2, Clock, ArrowRight } from 'lucide-react';

import { useTranslation } from '@/context/LanguageContext';


interface CommandItem {
  id: string;
  labelKey: string;
  descriptionKey?: string;
  href?: string;
  action?: () => void;
  icon: React.ElementType;
  categoryKey: string;
  keywords?: string[];
}

const COMMAND_ITEMS: CommandItem[] = [
  { id: 'nav-dashboard', labelKey: 'sidebar.items.dashboard', descriptionKey: 'commandPalette.items.dashboardDesc', href: '/call-dashboard', icon: LayoutDashboard, categoryKey: 'commandPalette.categories.navigation', keywords: ['home', 'principale'] },
  { id: 'nav-patients', labelKey: 'sidebar.items.patients', descriptionKey: 'commandPalette.items.patientsDesc', href: '/patient-management', icon: Users, categoryKey: 'commandPalette.categories.navigation', keywords: ['pazienti', 'anagrafica'] },
  { id: 'nav-calls', labelKey: 'sidebar.items.history', descriptionKey: 'commandPalette.items.callsDesc', href: '/call-history', icon: History, categoryKey: 'commandPalette.categories.navigation', keywords: ['chiamate', 'storico'] },
  { id: 'nav-calendar', labelKey: 'sidebar.items.calendar', descriptionKey: 'commandPalette.items.calendarDesc', href: '/calendar', icon: Calendar, categoryKey: 'commandPalette.categories.navigation', keywords: ['agenda', 'appuntamenti'] },
  { id: 'nav-analytics', labelKey: 'sidebar.items.analytics', descriptionKey: 'commandPalette.items.analyticsDesc', href: '/analytics', icon: BarChart2, categoryKey: 'commandPalette.categories.navigation', keywords: ['report', 'statistiche', 'grafici'] },
  { id: 'nav-followups', labelKey: 'sidebar.items.followups', descriptionKey: 'commandPalette.items.followupsDesc', href: '/follow-ups', icon: Phone, categoryKey: 'commandPalette.categories.navigation' },
  { id: 'nav-sms', labelKey: 'sidebar.items.sms', descriptionKey: 'commandPalette.items.smsDesc', href: '/sms', icon: MessageSquare, categoryKey: 'commandPalette.categories.navigation' },
  { id: 'nav-notifications', labelKey: 'nav.notifications', descriptionKey: 'commandPalette.items.notificationsDesc', href: '/notifications', icon: Bell, categoryKey: 'commandPalette.categories.navigation' },
  { id: 'nav-results', labelKey: 'sidebar.items.results', descriptionKey: 'commandPalette.items.resultsDesc', href: '/results', icon: FileText, categoryKey: 'commandPalette.categories.navigation' },
  { id: 'nav-exams', labelKey: 'sidebar.items.examCatalog', descriptionKey: 'commandPalette.items.examsDesc', href: '/exam-catalog', icon: FlaskConical, categoryKey: 'commandPalette.categories.navigation' },
  { id: 'nav-operators', labelKey: 'nav.operators', descriptionKey: 'commandPalette.items.operatorsDesc', href: '/operators', icon: Users, categoryKey: 'commandPalette.categories.navigation' },
  { id: 'nav-settings', labelKey: 'nav.settings', descriptionKey: 'commandPalette.items.settingsDesc', href: '/settings', icon: Settings, categoryKey: 'commandPalette.categories.navigation' },
  { id: 'nav-security', labelKey: 'sidebar.items.verifySecurity', descriptionKey: 'commandPalette.items.securityDesc', href: '/security/2fa', icon: Shield, categoryKey: 'commandPalette.categories.navigation' },
  { id: 'nav-branches', labelKey: 'sidebar.items.branches', descriptionKey: 'commandPalette.items.branchesDesc', href: '/branches', icon: Building2, categoryKey: 'commandPalette.categories.navigation' },
  { id: 'nav-urgent', labelKey: 'sidebar.items.urgent', descriptionKey: 'commandPalette.items.urgentDesc', href: '/urgent', icon: Zap, categoryKey: 'commandPalette.categories.navigation' },
  { id: 'nav-analytics-telecom', labelKey: 'sidebar.items.analyticsTelecom', descriptionKey: 'commandPalette.items.analyticsTelecomDesc', href: '/analytics/telecom', icon: BarChart2, categoryKey: 'commandPalette.categories.analytics' },
  { id: 'nav-twilio-health', labelKey: 'sidebar.items.twilioHealth', descriptionKey: 'commandPalette.items.twilioHealthDesc', href: '/twilio-health', icon: Shield, categoryKey: 'commandPalette.categories.twilio' },
  { id: 'nav-alert-notifications', labelKey: 'sidebar.items.alertNotifications', descriptionKey: 'commandPalette.items.alertNotificationsDesc', href: '/alert-notifications', icon: Bell, categoryKey: 'commandPalette.categories.twilio' },
  { id: 'nav-voice-intelligence', labelKey: 'sidebar.items.voiceIntelligence', descriptionKey: 'commandPalette.items.voiceIntelligenceDesc', href: '/voice-intelligence', icon: MessageSquare, categoryKey: 'commandPalette.categories.twilio' },
  { id: 'nav-numbers', labelKey: 'sidebar.items.numbersMgmt', descriptionKey: 'commandPalette.items.numbersDesc', href: '/numbers-management', icon: Phone, categoryKey: 'commandPalette.categories.twilio' },
];

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentItems, setRecentItems] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { t } = useTranslation();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('lcm_cmd_recent');
      if (stored) setRecentItems(JSON.parse(stored));
    }
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const filteredItems = query.trim()
    ? COMMAND_ITEMS.filter(item => {
        const q = query.toLowerCase();
        const label = t(item.labelKey).toLowerCase();
        const desc = item.descriptionKey ? t(item.descriptionKey).toLowerCase() : '';
        const cat = t(item.categoryKey).toLowerCase();
        return (
          label.includes(q) ||
          desc.includes(q) ||
          item.keywords?.some(k => k.toLowerCase().includes(q)) ||
          cat.includes(q)
        );
      })
    : COMMAND_ITEMS.filter(item => recentItems.includes(item.id)).slice(0, 5);

  const groupedItems = filteredItems.reduce<Record<string, CommandItem[]>>((acc, item) => {
    const cat = t(item.categoryKey);
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const flatItems = Object.values(groupedItems).flat();

  const executeItem = useCallback((item: CommandItem) => {
    const updated = [item.id, ...recentItems.filter(id => id !== item.id)].slice(0, 10);
    setRecentItems(updated);
    localStorage.setItem('lcm_cmd_recent', JSON.stringify(updated));

    if (item.action) {
      item.action();
    } else if (item.href) {
      router.push(item.href);
    }
    onClose();
  }, [recentItems, router, onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatItems[selectedIndex]) executeItem(flatItems[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [flatItems, selectedIndex, executeItem, onClose]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!open) return null;

  let flatIndex = 0;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh] px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('commandPalette.ariaLabel')}
    >
      <div
        className="w-full max-w-xl bg-white dark:bg-card border border-border rounded-2xl shadow-modal overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search size={16} className="text-muted-foreground shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('commandPalette.placeholder')}
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
            aria-label={t('commandPalette.searchAriaLabel')}
            aria-autocomplete="list"
            aria-controls="cmd-list"
            aria-activedescendant={flatItems[selectedIndex] ? `cmd-item-${flatItems[selectedIndex].id}` : undefined}
          />
          <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-[10px] font-mono text-muted-foreground">ESC</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} id="cmd-list" role="listbox" className="max-h-80 overflow-y-auto py-2">
          {!query && recentItems.length > 0 && flatItems.length > 0 && (
            <div className="px-3 py-1.5">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Clock size={10} aria-hidden="true" /> {t('commandPalette.recent')}
              </p>
            </div>
          )}
          {query && Object.keys(groupedItems).length === 0 && (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">{t('commandPalette.noResults')} &quot;{query}&quot;</p>
            </div>
          )}
          {Object.entries(groupedItems).map(([category, items]) => (
            <div key={category}>
              {query && (
                <div className="px-3 py-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{category}</p>
                </div>
              )}
              {items.map(item => {
                const currentIndex = flatIndex++;
                const isSelected = currentIndex === selectedIndex;
                const ItemIcon = item.icon;
                const label = t(item.labelKey);
                const description = item.descriptionKey ? t(item.descriptionKey) : undefined;
                return (
                  <button
                    key={item.id}
                    id={`cmd-item-${item.id}`}
                    data-index={currentIndex}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => executeItem(item)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${isSelected ? 'bg-primary/10' : 'hover:bg-muted/50'}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                      <ItemIcon size={14} aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{label}</p>
                      {description && <p className="text-xs text-muted-foreground truncate">{description}</p>}
                    </div>
                    {isSelected && <ArrowRight size={14} className="text-primary shrink-0" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-border flex items-center gap-4 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-muted border border-border rounded font-mono">↑↓</kbd> {t('commandPalette.navigate')}</span>
          <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-muted border border-border rounded font-mono">↵</kbd> {t('commandPalette.open')}</span>
          <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-muted border border-border rounded font-mono">ESC</kbd> {t('commandPalette.close')}</span>
        </div>
      </div>
    </div>
  );
}
